import { ReactElement, useMemo } from 'react'
import { Stack, Box } from '@mui/material'
import { UseMutateAsyncFunction } from '@tanstack/react-query'
import { ErrorFieldBase, Errors, Form } from '@ui/forms'
import SubmitButton from '@ui/forms/SubmitButton'
import {
  CrossSectionFormDataType,
  crossSectionFormDataToPayload,
  crossSectionFormSchema,
} from '../../schema'
import { FormFields } from '../FormFields'
import { getDefaultValues } from './schema'

interface Props {
  selectedElement: string
  defaultCS?: CrossSection
  elementType: ElementTypes
  mutateAsync: UseMutateAsyncFunction<unknown, unknown, CrossSection, unknown>
  isLoading: boolean
  isDisabled?: boolean
  warning?: string
  disableIfDirty?: boolean
}

const SingleCSForm = ({
  elementType,
  mutateAsync,
  isLoading,
  defaultCS,
  isDisabled = false,
  warning,
  disableIfDirty = true,
  selectedElement,
}: Props): ReactElement => {
  const handleSave = (crossSectionFormData: CrossSectionFormDataType) => {
    const crossSectionPayload = crossSectionFormDataToPayload(crossSectionFormData, {
      strict: true,
    })
    return mutateAsync(crossSectionPayload)
  }

  const defaultValues = useMemo(
    () => getDefaultValues(defaultCS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultCS, selectedElement],
  )

  return (
    <>
      <Form
        onSubmit={data => handleSave(data.crossSection)}
        defaultValues={defaultValues}
        validationSchema={crossSectionFormSchema}
        data-cy="crosssection-form"
        enableReinitialize
      >
        <Stack spacing={1}>
          <FormFields elementType={elementType} isDisabled={isDisabled} />

          {warning && (
            <Box mt={1}>
              <ErrorFieldBase severity="warning" message={warning} />
            </Box>
          )}

          <Errors />

          {!isDisabled && <SubmitButton loading={isLoading} disableIfNotDirty={disableIfDirty} />}
        </Stack>
      </Form>
    </>
  )
}

export default SingleCSForm
